<script>
//Repositories
import Repositories from '@/repositories';
const DashboardsRepository = Repositories.get('dashboards');

//Main components
import Layout from '@/router/layouts/main';
import Grid from './components/grid.vue';
import FiltersCard from '@/components/widgets/cards/filters-search';
import FieldsMenu from '@/components/widgets/grids/grid-fields-menu.vue';

//Mixins
import statusMixin from '@/mixins/statusMixin';

//Other components
import Multiselect from 'vue-multiselect';

export default {
  page: {
    title: 'DashboardPrices',
  },
  mixins: [statusMixin],
  components: {
    Layout,
    Grid,
    FiltersCard,
    Multiselect,
    FieldsMenu,
  },
  data() {
    return {
      filters: null,
      searchText: '',
      showFilters: false,
      selectedSuppliers: [],
      selectedCategories: [],
      selectedCategoryTypes: [],
      totalInventoryPrices: null,
    };
  },
  created() {
    this.search();
  },
  methods: {
    search() {
      this.filters = {
        suppliersIdPublic: this.selectedSuppliersIds,
        categoriesIdPublic: this.selectedCategoriesIds,
        categoryTypesIdPublic: this.selectedCategoryTypesIds,
        showNoPrice: this.showNoPrice,
        search: this.searchText,
      };

      this.totalInventoryPrices = null;
      DashboardsRepository.customREST
        .getTotalInventoryPrices({ ...this.filters, ...this.gridContext })
        .then(({ data }) => {
          this.totalInventoryPrices = data;
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    cancelSearch() {
      this.selectedSuppliers = [];
      this.selectedCategories = [];
      this.selectedCategoryTypes = [];
      this.searchText = '';
      this.showNoPrice = false;

      this.search();
    },
  },
  computed: {
    currencyFormatter() {
      return this.$store.state.cache.currencyFormatter;
    },
    fieldsComputed() {
      return [
        {
          key: 'code',
          label: this.$t('general.code'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('general.name'),
          sortable: true,
        },
        {
          key: 'totalQuantity',
          label: this.$t('items.totalQuantity'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'supplierName',
          label: this.$t('suppliers.supplier'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'defaultSupplierPrice',
          label: this.$t('items.price'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'totalOrderedPrice',
          label: this.$t('items.orderedPrice'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'totalInventoryPrice',
          label: this.$t('items.inventoryPrice'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'totalPrice',
          label: this.$t('items.totalPrice'),
          class: 'centered-field',
          sortable: true,
        },
      ];
    },
    dashboardPricesFiltersConfig: {
      get() {
        return this.$store.getters['localstorage/getByCode'](
          'dashboardPricesFiltersConfig'
        );
      },
      set(value) {
        this.$store.dispatch('localstorage/setByCode', {
          code: 'dashboardPricesFiltersConfig',
          config: value,
        });
      },
    },
    showNoPrice: {
      get() {
        return this.dashboardPricesFiltersConfig.showNoPrice;
      },
      set(value) {
        this.dashboardPricesFiltersConfig = {
          ...this.dashboardPricesFiltersConfig,
          showNoPrice: value,
        };
      },
    },
    suppliers() {
      return this.$store.state.cache.suppliers;
    },
    categories() {
      return this.$store.state.cache.categories;
    },
    categoryTypes() {
      return this.$store.state.cache.categoryTypes;
    },
    selectedSuppliersIds() {
      return this.selectedSuppliers != null
        ? this.selectedSuppliers.map((a) => a.idPublic)
        : [];
    },
    selectedCategoriesIds() {
      return this.selectedCategories != null
        ? this.selectedCategories.map((a) => a.idPublic)
        : [];
    },
    selectedCategoryTypesIds() {
      return this.selectedCategoryTypes != null
        ? this.selectedCategoryTypes.map((a) => a.idPublic)
        : [];
    },
  },
};
</script>

<template>
  <Layout
    :title="$t('dashboards.inventoryPrices')"
    :no-card="true"
    @esc-double-pressed="cancelSearch"
  >
    <div class="flex-box">
      <b-input-group class="search-bar custom-inventory-shadow">
        <b-form-input
          id="searchInput"
          type="text"
          style="border: none"
          :placeholder="$t('general.search')"
          @keydown.escape.prevent="cancelSearch()"
          @keydown.enter.prevent="search()"
          v-model="searchText"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            style="border: none"
            variant="outline-light"
            @click="search()"
          >
            <i class="bx bx-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <div class="flex-push box-wrapper">
        <div class="box custom-inventory-shadow">
          <div class="box-title">{{ $t('orders.ordered') }}</div>
          <div class="box-price">
            <span v-if="totalInventoryPrices != null">
              {{
                currencyFormatter.format(totalInventoryPrices.totalOrderedPrice)
              }}
            </span>
          </div>
        </div>
        <div class="box custom-inventory-shadow">
          <div class="box-title">{{ $t('inventory.inventory') }}</div>
          <div class="box-price">
            <span v-if="totalInventoryPrices != null">
              {{
                currencyFormatter.format(
                  totalInventoryPrices.totalInventoryPrice
                )
              }}
            </span>
          </div>
        </div>
        <div class="box custom-inventory-shadow">
          <div class="box-title">Total</div>
          <div class="box-price">
            <span v-if="totalInventoryPrices != null">
              {{ currencyFormatter.format(totalInventoryPrices.totalPrice) }}
            </span>
          </div>
        </div>
      </div>

      <FieldsMenu
        id="dashboard-prices-grid"
        :dropup="true"
        :fields="fieldsComputed"
      />
    </div>

    <FiltersCard @search="search" @cancel="cancelSearch">
      <template #row>
        <b-row>
          <b-col sm="12" lg="3">
            <b-form-group
              id="group-suppliers"
              :label="$t('suppliers.suppliers')"
              label-class="no-bold"
              label-for="supplierSelect"
            >
              <multiselect
                id="supplierSelect"
                ref="multiselectSupplier"
                v-model="selectedSuppliers"
                :options="suppliers"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectSupplier)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>

          <b-col sm="12" lg="3">
            <b-form-group
              id="group-categories"
              :label="$t('categories.categories')"
              label-class="no-bold"
              label-for="categorySelect"
            >
              <multiselect
                id="categorySelect"
                ref="multiselectCategory"
                v-model="selectedCategories"
                :options="categories"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectCategory)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>

          <b-col sm="12" lg="3">
            <b-form-group
              id="group-category-types"
              :label="$t('categories.categoryTypes')"
              label-class="no-bold"
              label-for="categoryTypeSelect"
            >
              <multiselect
                id="categoryTypeSelect"
                ref="multiselectCategoryType"
                v-model="selectedCategoryTypes"
                :options="categoryTypes"
                :label="'name'"
                track-by="idPublic"
                :placeholder="$t('general.pickAValue')"
                @remove="multiselectClosed($refs.multiselectCategoryType)"
                :multiple="true"
                :show-labels="false"
                :hide-selected="true"
                :close-on-select="false"
                :clear-on-select="false"
                :searchable="false"
              >
                <template v-slot:noOptions>
                  {{ $t('general.noResult') }}
                </template>
                <template slot="tag" slot-scope="{ option, remove }">
                  <span
                    class="custom__tag badge badge-primary badge-pill"
                    style="font-size: 1em; margin-right: 5px; cursor: pointer"
                    v-html="option.name"
                    @click="remove(option)"
                  >
                  </span>
                </template>
              </multiselect>
            </b-form-group>
          </b-col>

          <b-col sm="12" lg="3" class="flex-vertical-align-bottom">
            <div style="padding-bottom: 18px">
              <b-form-checkbox
                id="checkbox-show-no-price"
                v-model="showNoPrice"
                name="checkbox-show-no-price"
                switch
              >
                <span class="no-bold">{{ $t('dashboards.showNoPrice') }}</span>
              </b-form-checkbox>
            </div>
          </b-col>
        </b-row>
      </template>
    </FiltersCard>

    <Grid
      v-if="filters != null"
      :filters="filters"
      :fields="fieldsComputed"
      :totalInventoryPrices="totalInventoryPrices"
    />
  </Layout>
</template>

<style scoped>
.search-bar {
  max-width: 500px;
  max-height: 40px;
  background-color: white;
  border-radius: 5px;
}

.box-wrapper {
  display: flex;
  margin-top: -20px;
}

.box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  width: 200px;
  background-color: white;
  margin-right: 12px;
  border-radius: 5px;
}

.box-title {
  font-weight: 600;
  font-size: 1.1em;
}

.box-wrapper {
  font-size: 1.1em;
}
</style>